import { useEffect, useState } from 'react'
import { Input, Tabs } from '@iveco-ti/iveco-ds'
import { FormProvider, UseFormReturn } from '@iveco-ti/iveco-ds/hook-form'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  ChangePasswordForm as ChangePasswordFormType,
  EditCredentialsForm as EditCredentialsFormType,
} from '../schemas/EditCredentialsSchema'

type EditCredentialsFormProps = {
  credentialsForm: UseFormReturn<EditCredentialsFormType>
  passwordForm: UseFormReturn<ChangePasswordFormType>
  onSubmitCredentials: (data: EditCredentialsFormType) => void
  onSubmitPassword: (data: ChangePasswordFormType) => void
  activeTab: string
  onActiveTabChange: (tab: string) => void
}

export function EditCredentialsForm({
  credentialsForm,
  passwordForm,
  onSubmitCredentials,
  onSubmitPassword,
  activeTab,
  onActiveTabChange,
}: EditCredentialsFormProps) {
  const { t } = useTranslation(['sso', 'common'])
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    setShowCurrentPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
  }, [activeTab])

  return (
    <Tabs.Root
      defaultValue="credentials"
      value={activeTab}
      className="h-fit"
      onValueChange={onActiveTabChange}
    >
      <Tabs.List className="flex items-center gap-10 mb-6">
        <Tabs.Trigger value="credentials">
          {t(
            'sso:applications.drawer_manage_auth.edit_credentials.tabs.credentials',
          )}
        </Tabs.Trigger>
        <Tabs.Trigger value="password">
          {t(
            'sso:applications.drawer_manage_auth.edit_credentials.tabs.password',
          )}
        </Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value="credentials">
        <FormProvider {...credentialsForm}>
          <form
            id="edit-credentials-form"
            autoComplete="new-password"
            className="space-y-6"
            onSubmit={credentialsForm.handleSubmit(onSubmitCredentials)}
          >
            <Input.Base
              name="clientKey"
              label={t(
                'sso:applications.drawer_manage_auth.fields.client_key.label',
              )}
              autoComplete="new-password"
            />

            <Input.Icon
              name="currentPassword"
              type={showCurrentPassword ? 'text' : 'password'}
              label={t(
                'sso:applications.drawer_manage_auth.edit_credentials.fields.current_password.label',
              )}
              placeholder="**************"
              iconPosition="right"
              autoComplete="new-password"
              icon={
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <Eye size={16} />
                  ) : (
                    <EyeOff size={16} />
                  )}
                </button>
              }
            />
          </form>
        </FormProvider>
      </Tabs.Content>

      <Tabs.Content value="password">
        <FormProvider {...passwordForm}>
          <form
            id="change-password-form"
            autoComplete="new-password"
            className="space-y-6"
            onSubmit={passwordForm.handleSubmit(onSubmitPassword)}
          >
            <Input.Icon
              name="currentPassword"
              type={showCurrentPassword ? 'text' : 'password'}
              label={t(
                'sso:applications.drawer_manage_auth.edit_credentials.fields.current_password.label',
              )}
              placeholder="**************"
              iconPosition="right"
              autoComplete="new-password"
              icon={
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <Eye size={16} />
                  ) : (
                    <EyeOff size={16} />
                  )}
                </button>
              }
            />

            <Input.Icon
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              label={t(
                'sso:applications.drawer_manage_auth.edit_credentials.fields.new_password.label',
              )}
              placeholder="**************"
              iconPosition="right"
              autoComplete="new-password"
              icon={
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              }
            />

            <Input.Icon
              name="confirmNewPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              label={t(
                'sso:applications.drawer_manage_auth.edit_credentials.fields.confirm_password.label',
              )}
              placeholder="**************"
              iconPosition="right"
              autoComplete="new-password"
              icon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <Eye size={16} />
                  ) : (
                    <EyeOff size={16} />
                  )}
                </button>
              }
            />
          </form>
        </FormProvider>
      </Tabs.Content>
    </Tabs.Root>
  )
}
